import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { CheckCircle, Table, SquaresFour, Flame } from "@phosphor-icons/react";
import { useSiteContent } from "../../contexts/SiteContentContext";
import { DEFAULT_SITE_CONTENT } from "../../lib/siteContentDefaults";

const SERVICE_IMAGES = {
  "Functional Fitness Monthly": "https://images.unsplash.com/photo-1758875569399-99a7d80ace43",
  "Sports Specific Performance": "https://images.unsplash.com/photo-1758875569414-120ebc62ada3",
  "Physique Transformation": "https://images.unsplash.com/photo-1758875570137-8691b7c55033",
  "Small Group Training": "https://images.unsplash.com/photo-1554284126-aa88f22d8b74",
  "Corporate & Tech Executive Wellness": "https://images.pexels.com/photos/7971555/pexels-photo-7971555.jpeg",
  "Nutrition Coaching": "https://images.unsplash.com/photo-1543352632-5a4b24e4d2a6",
};

const GROUPS = [
  {
    key: "intro",
    names: ["Free Trial Session", "3-Class Pass", "5-Class Pass", "10-Class Pass"],
  },
  {
    key: "memberships",
    names: ["Small Group Training", "Functional Fitness Monthly", "Physique Transformation", "Sports Specific Performance"],
  },
  {
    key: "addons",
    names: ["Nutrition Coaching", "Travel / In-Home Add-On", "Corporate & Tech Executive Wellness"],
  },
];

const COL_CLASS = { 2: "md:grid-cols-2 lg:grid-cols-2", 3: "md:grid-cols-2 lg:grid-cols-3", 4: "md:grid-cols-2 lg:grid-cols-4" };

function BookModal({ pkg, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);

  const claim = async () => {
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, type: "booking", package_name: pkg.name, message: `Claimed: ${pkg.name}` });
      toast.success("Request sent! Coach Conde will follow up shortly.");
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center px-6" onClick={onClose}>
      <div data-testid="claim-trial-modal" onClick={(e) => e.stopPropagation()} className="bg-[#141414] border border-[#27272A] rounded-2xl p-8 max-w-md w-full">
        <h3 className="font-heading text-2xl text-white tracking-tight mb-1">Claim: {pkg.name}</h3>
        <p className="text-sm text-[#A1A1AA] mb-6">We'll text/email you to lock in a time.</p>
        <div className="space-y-3 mb-6">
          <input data-testid="claim-name-input" placeholder="Full Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-xl px-4 py-3 text-white placeholder:text-[#71717A] focus:outline-none focus:border-[#D4AF37]" />
          <input data-testid="claim-email-input" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-xl px-4 py-3 text-white placeholder:text-[#71717A] focus:outline-none focus:border-[#D4AF37]" />
          <input data-testid="claim-phone-input" placeholder="Phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-xl px-4 py-3 text-white placeholder:text-[#71717A] focus:outline-none focus:border-[#D4AF37]" />
        </div>
        <div className="flex gap-3">
          <button data-testid="claim-cancel-button" onClick={onClose} className="flex-1 border border-white/20 text-white rounded-full py-3 hover:bg-white/10 transition-colors">Cancel</button>
          <button data-testid="claim-submit-button" disabled={!form.name || !form.email || submitting} onClick={claim} className="flex-1 bg-[#D4AF37] disabled:opacity-30 text-black font-semibold rounded-full py-3 hover:bg-white transition-colors">
            {submitting ? "Sending..." : "Claim It"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PackageCard({ pkg, image, onSelect, loading }) {
  return (
    <div
      data-testid={`package-card-${pkg.id}`}
      className={`relative rounded-2xl border overflow-hidden flex flex-col bg-[#141414] transition-transform hover:-translate-y-1 h-full ${
        pkg.is_popular ? "border-[#D4AF37]" : "border-[#27272A]"
      }`}
    >
      {pkg.is_popular && (
        <span className="absolute top-4 right-4 bg-[#D4AF37] text-black text-xs font-semibold uppercase tracking-wide px-3 py-1 rounded-full z-10">
          Most Popular
        </span>
      )}
      {image && <img data-testid={`package-card-image-${pkg.id}`} src={image} alt={pkg.name} className="w-full h-40 object-cover shrink-0" />}
      <div className="p-6 flex flex-col flex-1">
        <p className="text-xs uppercase tracking-[0.15em] text-[#71717A] mb-1">{pkg.location_type} &middot; {pkg.billing_cycle}</p>
        <h3 className="font-heading text-2xl text-white tracking-tight mb-2">{pkg.name}</h3>
        <p className="text-sm text-[#A1A1AA] mb-4 leading-relaxed">{pkg.description}</p>
        <div className="mb-4">
          <span className="font-heading text-3xl text-white">{pkg.price > 0 ? `$${pkg.price}` : "Free"}</span>
          {pkg.billing_cycle === "Monthly" && pkg.price > 0 && <span className="text-sm text-[#71717A]">/mo</span>}
          {pkg.billing_cycle === "Weekly" && pkg.price > 0 && <span className="text-sm text-[#71717A]">/wk</span>}
        </div>
        <ul className="space-y-2 mb-6 flex-1">
          {pkg.features.map((f, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-[#D4D4D8]">
              <CheckCircle size={16} weight="fill" className="text-[#D4AF37] mt-0.5 shrink-0" /> {f}
            </li>
          ))}
        </ul>
        <button
          data-testid={`package-select-button-${pkg.id}`}
          disabled={loading}
          onClick={() => onSelect(pkg)}
          className="w-full bg-white text-black font-semibold rounded-full py-3 hover:bg-[#D4AF37] transition-colors disabled:opacity-50"
        >
          {loading ? "Redirecting..." : pkg.price > 0 ? "Select Package" : "Claim Free Trial"}
        </button>
      </div>
    </div>
  );
}

function ComparisonTable({ items, onSelect, loading }) {
  const cellBg = (pkg) => (pkg.is_popular ? "bg-[#D4AF37]/[0.06]" : "");
  return (
    <div data-testid="services-comparison-table" className="overflow-x-auto rounded-2xl border border-[#27272A]">
      <table className="w-full text-sm border-collapse min-w-[720px]">
        <thead>
          <tr className="bg-[#141414]">
            <th className="text-left p-5 text-[#71717A] font-normal w-40">Plan</th>
            {items.map((pkg) => (
              <th
                key={pkg.id}
                className={`text-left p-5 min-w-[180px] align-bottom ${pkg.is_popular ? "bg-[#D4AF37]/10" : ""}`}
              >
                {pkg.is_popular && (
                  <motion.span
                    data-testid={`compare-most-popular-badge-${pkg.id}`}
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: [0, -3, 0] }}
                    transition={{ y: { duration: 1.6, repeat: Infinity, ease: "easeInOut" }, opacity: { duration: 0.4 } }}
                    className="inline-flex items-center gap-1 mb-2 bg-[#D4AF37] text-black text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full shadow-[0_0_14px_rgba(212,175,55,0.65)]"
                  >
                    <Flame size={11} weight="fill" /> Most Popular
                  </motion.span>
                )}
                <span className="font-heading text-lg text-white tracking-tight block">{pkg.name}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className="border-t border-[#27272A]">
            <td className="p-5 text-[#71717A]">Price</td>
            {items.map((pkg) => (
              <td key={pkg.id} className={`p-5 ${cellBg(pkg)}`}>
                <span className="font-heading text-xl text-white">${pkg.price}</span>
                <span className="text-xs text-[#71717A]">/mo</span>
              </td>
            ))}
          </tr>
          <tr className="border-t border-[#27272A] bg-[#141414]/60">
            <td className="p-5 text-[#71717A]">Focus</td>
            {items.map((pkg) => (
              <td key={pkg.id} className={`p-5 text-[#D4D4D8] ${cellBg(pkg)}`}>{pkg.description}</td>
            ))}
          </tr>
          {[0, 1, 2, 3].map((i) => (
            <tr key={i} className={`border-t border-[#27272A] ${i % 2 === 1 ? "bg-[#141414]/60" : ""}`}>
              <td className="p-5 text-[#71717A]">Includes</td>
              {items.map((pkg) => (
                <td key={pkg.id} className={`p-5 text-[#D4D4D8] ${cellBg(pkg)}`}>
                  {pkg.features[i] ? (
                    <span className="flex items-start gap-2">
                      <CheckCircle size={16} weight="fill" className="text-[#D4AF37] mt-0.5 shrink-0" /> {pkg.features[i]}
                    </span>
                  ) : (
                    <span className="text-[#52525B]">&mdash;</span>
                  )}
                </td>
              ))}
            </tr>
          ))}
          <tr className="border-t border-[#27272A]">
            <td className="p-5" />
            {items.map((pkg) => (
              <td key={pkg.id} className={`p-5 ${cellBg(pkg)}`}>
                <button
                  data-testid={`compare-select-button-${pkg.id}`}
                  disabled={loading === pkg.id}
                  onClick={() => onSelect(pkg)}
                  className={`font-semibold text-sm rounded-full px-5 py-2.5 transition-colors disabled:opacity-50 ${
                    pkg.is_popular ? "bg-[#D4AF37] text-black hover:bg-white" : "bg-white text-black hover:bg-[#D4AF37]"
                  }`}
                >
                  {loading === pkg.id ? "Redirecting..." : "Select Package"}
                </button>
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function Services() {
  const c = { ...DEFAULT_SITE_CONTENT, ...useSiteContent() };
  const [packages, setPackages] = useState([]);
  const [modalPkg, setModalPkg] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const [compareMode, setCompareMode] = useState(false);

  useEffect(() => {
    api.get("/packages").then(({ data }) => setPackages(data)).catch(() => {});
  }, []);

  const handleSelect = async (pkg) => {
    if (!pkg.price || pkg.price <= 0) {
      setModalPkg(pkg);
      return;
    }
    setLoadingId(pkg.id);
    try {
      const { data } = await api.post("/payments/checkout", { package_id: pkg.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Checkout unavailable, please book a free call instead");
    } finally {
      setLoadingId(null);
    }
  };

  const grouped = GROUPS.map((g) => ({ ...g, items: g.names.map((n) => packages.find((p) => p.name === n)).filter(Boolean) })).filter(
    (g) => g.items.length
  );
  const groupedNames = new Set(GROUPS.flatMap((g) => g.names));
  const otherItems = packages.filter((p) => !groupedNames.has(p.name));
  const imageFor = (pkg) => (pkg.name === "Travel / In-Home Add-On" ? c.service_image_travel_addon : SERVICE_IMAGES[pkg.name]);

  return (
    <section id="services" className="py-24 px-6 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto">
        <div className="mb-14">
          <p data-testid="services-eyebrow" className="font-heading uppercase tracking-[0.2em] text-xs text-[#D4AF37] mb-2">{c.services_eyebrow}</p>
          <h2 data-testid="services-title" className="font-heading text-3xl sm:text-4xl md:text-5xl text-white tracking-tight">{c.services_title}</h2>
        </div>

        {grouped.map((g, gi) => (
          <div key={g.key} data-testid={`services-group-${g.key}`} className={gi > 0 ? "mt-16" : ""}>
            <div className="flex flex-wrap items-baseline justify-between gap-4 mb-1">
              <div className="flex items-baseline gap-3">
                <span className="font-heading text-sm text-[#D4AF37]">0{gi + 1}</span>
                <h3 data-testid={`services-group-title-${g.key}`} className="font-heading text-2xl sm:text-3xl text-white tracking-tight">{c[`services_group_${g.key}_title`]}</h3>
              </div>
              {g.key === "memberships" && (
                <button
                  data-testid="services-compare-toggle"
                  onClick={() => setCompareMode((prev) => !prev)}
                  className="flex items-center gap-2 text-sm border border-[#27272A] text-[#A1A1AA] hover:border-[#D4AF37]/60 hover:text-white rounded-full px-4 py-2 transition-colors"
                >
                  {compareMode ? <SquaresFour size={16} /> : <Table size={16} />}
                  {compareMode ? "Show Cards" : "Compare Plans"}
                </button>
              )}
            </div>
            <p data-testid={`services-group-subtitle-${g.key}`} className="text-sm text-[#71717A] mb-6">{c[`services_group_${g.key}_subtitle`]}</p>
            {g.key === "memberships" && compareMode ? (
              <ComparisonTable items={g.items} onSelect={handleSelect} loading={loadingId} />
            ) : (
              <div className={`grid ${COL_CLASS[g.items.length] || COL_CLASS[4]} gap-6 items-stretch`}>
                {g.items.map((pkg) => (
                  <PackageCard key={pkg.id} pkg={pkg} image={imageFor(pkg)} onSelect={handleSelect} loading={loadingId === pkg.id} />
                ))}
              </div>
            )}
          </div>
        ))}

        {otherItems.length > 0 && (
          <div data-testid="services-group-other" className="mt-16">
            <div className="flex items-baseline gap-3 mb-1">
              <span className="font-heading text-sm text-[#D4AF37]">0{grouped.length + 1}</span>
              <h3 className="font-heading text-2xl sm:text-3xl text-white tracking-tight">More Options</h3>
            </div>
            <p className="text-sm text-[#71717A] mb-6">Additional packages Coach Conde offers.</p>
            <div className={`grid ${COL_CLASS[otherItems.length] || COL_CLASS[4]} gap-6 items-stretch`}>
              {otherItems.map((pkg) => (
                <PackageCard key={pkg.id} pkg={pkg} image={imageFor(pkg)} onSelect={handleSelect} loading={loadingId === pkg.id} />
              ))}
            </div>
          </div>
        )}
      </div>
      {modalPkg && <BookModal pkg={modalPkg} onClose={() => setModalPkg(null)} />}
    </section>
  );
}
